import { PropsWithChildren, useMemo } from "react";

import { css } from "@compiled/react";

import {
    AtlassianLogo,
    ConfluenceLogo,
    JiraLogo,
    JiraServiceManagementLogo,
    LoomLogo
} from "@atlaskit/logo";
import { media } from "@atlaskit/primitives/responsive";
import { token } from "@atlaskit/tokens";

import { Input } from "@/components/signup/account-creation/components/input/input";
import { Social } from "@/components/signup/account-creation/components/social/social";
import { Submit } from "@/components/signup/account-creation/components/submit/submit";
import { useUserContext } from "@/components/user-context/user-context-provider";

export const WhiteScreenLayout = ({
    children,
    heading1,
    heading2
}: PropsWithChildren<{
    heading1: string;
    heading2: string;
}>) => {
    return (
        <div css={wrapper}>
            <div css={container}>
                <ProductLogo />
                <h1 css={heading1Css}>{heading1}</h1>
                <p css={heading2Css}>{heading2}</p>
                {children}
            </div>
            <div css={footer}>
                <AtlassianLogo
                    appearance="brand"
                    size="small"
                    testId="atlassian-logo"
                    label="Atlassian logo"
                />
            </div>
        </div>
    );
};

export const WhiteScreenInput = ({ helpText }: { helpText?: string }) => (
    <div css={inputCss}>
        <Input helpText={helpText} />
    </div>
);

export const WhiteScreenSocial = () => (
    <div css={socialCss}>
        <Social />
    </div>
);

export const WhiteScreenSubmit = () => (
    <div css={submitCss}>
        <Submit />
    </div>
);

const ProductLogo = () => {
    const { product } = useUserContext();
    return useMemo(() => {
        let logo;
        if (product?.name === "jira") {
            logo = <JiraLogo appearance="brand" size="large" />;
        }
        if (product?.name === "confluence") {
            logo = <ConfluenceLogo appearance="brand" size="large" />;
        }
        if (product?.name === "jira-servicedesk") {
            logo = (
                <JiraServiceManagementLogo appearance="brand" size="large" />
            );
        }
        if (product?.name === "loom") {
            logo = <LoomLogo appearance="brand" size="large" />;
        }
        if (logo) {
            return <div css={logoContainerCss}>{logo}</div>;
        }
        return null;
    }, [product?.name]);
};

const container = css({
    color: token("color.text.subtle"),
    flex: 1,
    maxWidth: "25rem"
});

const footer = css({
    margin: "1.5rem auto"
});

const headingStyles = {
    color: token("color.text"),
    fontFamily: "var(--atlassian-recommended-system-fonts)", // needed to override index.css
    textAlign: "center"
};

const heading1Css = css({
    ...headingStyles,
    fontSize: "2.25rem",
    fontWeight: 600,
    lineHeight: 1.15,
    margin: 0,
    [media.above.sm]: {
        margin: "0 -3rem" // "sm" is 48rem, maxWidth is 25rem (> 3rem on each side)
    }
});

const heading2Css = css({
    ...headingStyles,
    fontSize: "inherit",
    fontWeight: "normal",
    margin: "0.5rem 0 2rem"
});

const inputCss = css({
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    ["div[role='presentation']"]: {
        borderRadius: 3
    },
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    ["input#email-uid1"]: {
        paddingInlineStart: "6px !important",
        fontFamily: "var(--atlassian-recommended-system-fonts) !important"
    },
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    label: {
        fontFamily: "var(--atlassian-recommended-system-fonts) !important"
    }
});

const logoContainerCss = css({
    display: "flex",
    justifyContent: "center",
    marginBlockEnd: "1.25rem"
});

const socialCss = css({
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    button: {
        border: 0,
        borderRadius: 3,
        // eslint-disable-next-line local/ensure-wac-token-usage
        boxShadow: "0px 1px 1px 0px #091E4240, 0px 0px 1px 0px #091E424F"
    }
});

const submitCss = css({
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    button: {
        borderRadius: 3,
        fontFamily: "var(--atlassian-recommended-system-fonts) !important",
        fontSize: 14,
        minHeight: 40,
        padding: 6
    }
});

const wrapper = css({
    alignItems: "center",
    boxSizing: "border-box",
    display: "flex",
    flexDirection: "column",
    fontFamily: "var(--atlassian-recommended-system-fonts)",
    fontSize: "0.875rem",
    justifyContent: "space-between",
    minHeight: "80vh",
    padding: "1.5rem 1rem",
    [media.above.sm]: {
        minHeight: "95vh"
    }
});
